import {
  ChatDrawer,
  CustomerDetailsCard,
  JobDetailsCard,
  ServiceHistoryModal,
  OrderHeaderCards,
  OrderInvoiceCard,
  OrderPresenceAlert,
  OrderServiceSelect,
  OrderToolbar,
  RegPlate,
} from '@components';
import { useView } from '@hooks';
import {
  LocalizedServiceOrderStatus,
  LocalizedServiceOrderStatusObjectKey,
  OrderStatus,
  ServiceOrderStatusColor,
  ServiceOrderStatusColorObjectKey,
} from '@models';
import { useStore } from '@stores';
import { Tag } from 'antd';
import { observer } from 'mobx-react';
import { useState } from 'react';

export const OrderView = observer(() => {
  const { orderStore, vehicleStore, authenticationStore } = useStore();
  const { order } = orderStore;
  const vehicle = vehicleStore.vehicle;
  const userRole = authenticationStore.currentUser?.role;
  const { isMobile } = useView();

  const [openMileageModal, setOpenMileageModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [workPickerOpen, setWorkPickerOpen] = useState(false);
  // Print is an order-wide action like Add work, so the toolbar holds them side by side.
  const [printsSlot, setPrintsSlot] = useState<HTMLSpanElement | null>(null);

  const shouldHideManagerFeatures = userRole === 'Technician';
  // Same rule OrderServiceSelect applies to its own select: no edits with an estimate out or once invoiced.
  const servicesLocked =
    !!order &&
    (order.status === OrderStatus.Approval ||
      order.status === OrderStatus.Paid ||
      order.status === OrderStatus.Ready);

  if (!order || !vehicle) return null;

  const jobCard = <JobDetailsCard />;

  const customerCard = (
    <CustomerDetailsCard
      compact
      customer={order.customer}
      vehicle={vehicle}
      onOpenChat={() => setChatOpen(true)}
      currentMileage={order.mileage}
    />
  );

  const invoiceCardContent = (
    <OrderInvoiceCard order={order} vehicle={vehicle} iconOnly />
  );

  // Collapsed-panel facts for the phone layout (the desktop cards show them in full).
  const statusLabel =
    LocalizedServiceOrderStatus[order.status as LocalizedServiceOrderStatusObjectKey] || order.status;
  const statusColor = ServiceOrderStatusColor[order.status as ServiceOrderStatusColorObjectKey];
  const jobSummary = (
    <>
      <Tag color={statusColor} style={{ margin: 0, color: 'rgba(0,0,0,0.75)' }}>
        {statusLabel}
      </Tag>
      <span>{order.user?.fullname ?? 'Unassigned'}</span>
    </>
  );
  const customerSummary = (
    <>
      <span>{order.customer?.fullname}</span>
      {vehicle.registrationNumber && <RegPlate size="small">{vehicle.registrationNumber}</RegPlate>}
    </>
  );
  // The invoice itself is only fetched once the panel opens, so the state comes from the order:
  // Ready = invoiced and awaiting payment; Completed = the job is done and can be invoiced.
  const invoiceSummary =
    order.status === OrderStatus.Paid
      ? 'Paid'
      : order.status === OrderStatus.Ready
        ? 'Invoiced - awaiting payment'
        : order.status === OrderStatus.Completed
          ? 'Ready to invoice'
          : 'Not invoiced';

  return (
    <>
      <OrderPresenceAlert orderId={order?.id} />

      {!shouldHideManagerFeatures && (
        <OrderHeaderCards
          expanded={orderStore.orderDetailsExpanded}
          jobCard={jobCard}
          customerCard={customerCard}
          invoiceCard={invoiceCardContent}
          jobSummary={jobSummary}
          customerSummary={customerSummary}
          invoiceSummary={invoiceSummary}
        />
      )}

      {/* The job itself - services, labour and parts - is the page; everything else is a drawer
          opened from the row at the top of it. */}
      <div
        className={isMobile ? 'order-tabs-mobile' : undefined}
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: isMobile ? '8px 16px 16px' : '12px 16px 16px',
          marginTop: isMobile ? 0 : 12,
        }}
      >
        <OrderToolbar
          order={order}
          vehicle={vehicle}
          onAddWork={servicesLocked ? undefined : () => setWorkPickerOpen(true)}
          addWorkLockedReason={
            servicesLocked
              ? order.status === OrderStatus.Approval
                ? 'Cannot add work while the estimate is out - revise it instead'
                : 'This job is invoiced, so its work cannot be changed'
              : undefined
          }
          printsRef={setPrintsSlot}
        />
        {/* Totals are in the drawer footer and the invoice card, so no strip here; services are
            added from the toolbar's "Add work", which opens the picker this component owns. */}
        <OrderServiceSelect
          atLeastOneService
          hideHeader
          pickerOpen={workPickerOpen}
          actionsSlot={printsSlot}
          onPickerClose={() => setWorkPickerOpen(false)}
        />
      </div>

      <ServiceHistoryModal
        orderId={order.id}
        vin={vehicle.combinedVin}
        mileage={order.mileage}
        customerId={order.customerId}
        open={openMileageModal}
        setOpen={setOpenMileageModal}
        onCancel={() => setOpenMileageModal(false)}
      />

      {order.customer?.userId && (
        <ChatDrawer
          open={chatOpen}
          channelId={order.requestId ?? order.id}
          customerUserId={order.customer.userId}
          title={`Chat with ${order.customer.fullname}`}
          onClose={() => setChatOpen(false)}
        />
      )}
    </>
  );
});
